import axios from '@/_utils/axios';
import { BE2FEAssemblyTemplate, mapContentAssemblyToTemplateData } from '@/components/Packaging/utils';
import { LoadingStatuses } from '@/constants/constants';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  AssemblyTemplate,
  AssemblyTemplateBEResponse,
  ContentAssembly,
  InventoryFileWithProxy,
  type MovingOverlayData,
  OptionItem,
  type PackagingState,
  type TimelineItem
} from './packagingSlice.types';
import { PolarisApiResponse } from './slice_types';

const initialState: PackagingState = {
  toolboxOptions: [],
  isToolboxDrawerOpen: false,

  loadingStatus: null,
  timelineScale: 1,
  timelineFrameRate: [30000, 1001], // 29.97 fps
  isEditingTimeline: false,
  movingOverlayData: null,
  playerCurrentFrame: 0,
  isPlayerOpened: false,
  isPlaying: false,
  activeTemplate: null,
  activeCutType: null,
  activeHover: null,

  inventoryAssignmentModal: {
    selectedFile: null,
    previewedFile: null
  },

  contentAssembly: null
};

export const getContentAssemblyObject = createAsyncThunk('packaging/getContentAssemblyObject', async (taskId: string, { rejectWithValue }) => {
  return axios
    .get<PolarisApiResponse<ContentAssembly>>(`/content_assembly/${taskId}/`)
    .then((res) => res.data.data)
    .catch(() => {
      console.warn('Could not get content assembly object');
      return rejectWithValue('Could not get content assembly object');
    });
});

export const getAssemblyTemplateObject = createAsyncThunk('packaging/getAssemblyTemplateObject', async (templateId: string, { rejectWithValue }) => {
  return axios
    .get<PolarisApiResponse<AssemblyTemplateBEResponse>>(`/assembly_templates/${templateId}/`)
    .then((res): AssemblyTemplate => {
      return BE2FEAssemblyTemplate(res.data.data);
    })
    .catch(() => {
      console.warn('Could not get assembly template object');
      return rejectWithValue('Could not get assembly template object');
    });
});

type SetInventoryModalSelectedFileAction = PayloadAction<InventoryFileWithProxy | null>;
type SetInventoryModalPreviewedFileAction = PayloadAction<InventoryFileWithProxy | null>;

const packagingSlice = createSlice({
  name: 'packaging',
  initialState,
  reducers: {
    setTimelineScale: (state, action: PayloadAction<number>) => {
      state.timelineScale = action.payload;
    },

    setMovingOverlayData: (state, { payload }: PayloadAction<MovingOverlayData>) => {
      state.movingOverlayData = payload;
    },
    clearMovingOverlayData: (state) => {
      state.movingOverlayData = null;
    },

    setIsEditingTimeline: (state, action: PayloadAction<boolean>) => {
      state.isEditingTimeline = action.payload;
    },

    setPlayerCurrentFrame: (state, action: PayloadAction<number>) => {
      state.playerCurrentFrame = action.payload;
    },

    setIsPlayerOpened: (state, action: PayloadAction<boolean>) => {
      state.isPlayerOpened = action.payload;
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      state.isPlaying = action.payload;
    },

    setToolboxOptions: (state, action: PayloadAction<OptionItem[]>) => {
      state.toolboxOptions = action.payload;
    },
    setIsToolboxDrawerOpen: (state, action: PayloadAction<boolean>) => {
      state.isToolboxDrawerOpen = action.payload;
    },

    setActiveTemplate: (state, action: PayloadAction<AssemblyTemplate | null>) => {
      state.activeTemplate = action.payload;
    },
    setActiveCutType: (state, action: PayloadAction<TimelineItem | null>) => {
      state.activeCutType = action.payload;
    },
    setActiveHover: (state, action: PayloadAction<TimelineItem[] | null>) => {
      state.activeHover = action.payload;
    },
    clearActiveHover: (state) => {
      state.activeHover = null;
    },
    setInventoryModalSelectedFile: (state, action: SetInventoryModalSelectedFileAction) => {
      state.inventoryAssignmentModal.selectedFile = action.payload;
    },
    setInventoryModalPreviewedFile: (state, action: SetInventoryModalPreviewedFileAction) => {
      state.inventoryAssignmentModal.previewedFile = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getContentAssemblyObject.pending, (state) => {
      state.loadingStatus = LoadingStatuses.pending;
    });
    builder.addCase(getContentAssemblyObject.fulfilled, (state, action) => {
      state.loadingStatus = LoadingStatuses.fulfilled;
      state.contentAssembly = action.payload;

      if (action.payload) {
        const { assembly_template_inputs } = action.payload;

        const mappedData = mapContentAssemblyToTemplateData(assembly_template_inputs);

        // TODO: review this code. It feels like we don't need to store all that data in the redux store.
        state.activeTemplate = {
          id: action.payload.assembly_template_inputs.id ?? null,
          name: action.payload.assembly_template_inputs.name ?? '',
          slug: action.payload.assembly_template_inputs.slug ?? '',
          template_type: action.payload.assembly_template_inputs.template_type ?? '',
          description: action.payload.assembly_template_inputs.description ?? '',
          data: mappedData,
          created_at: action.payload.assembly_template_inputs.created_at ?? '',
          updated_at: action.payload.assembly_template_inputs.updated_at ?? '',
          deleted_at: action.payload.assembly_template_inputs.deleted_at ?? null,
          created_by: action.payload.assembly_template_inputs.created_by ?? null,
          updated_by: action.payload.assembly_template_inputs.updated_by ?? null,
          deleted_by: action.payload.assembly_template_inputs.deleted_by ?? null
        };
      } else {
        state.activeTemplate = null;
      }
    });
    builder.addCase(getContentAssemblyObject.rejected, (state) => {
      state.loadingStatus = LoadingStatuses.rejected;
    });
    builder.addCase(getAssemblyTemplateObject.pending, (state) => {
      state.loadingStatus = LoadingStatuses.pending;
    });
    builder.addCase(getAssemblyTemplateObject.fulfilled, (state, action) => {
      state.loadingStatus = LoadingStatuses.fulfilled;
      state.activeTemplate = action.payload;
    });
    builder.addCase(getAssemblyTemplateObject.rejected, (state) => {
      state.loadingStatus = LoadingStatuses.rejected;
    });
  }
});

export const {
  setTimelineScale,
  setIsEditingTimeline,
  setMovingOverlayData,
  clearMovingOverlayData,
  setPlayerCurrentFrame,
  setIsPlayerOpened,
  setIsPlaying,

  setToolboxOptions,
  setIsToolboxDrawerOpen,

  setActiveTemplate,
  setActiveCutType,
  setActiveHover,
  clearActiveHover,

  setInventoryModalSelectedFile,
  setInventoryModalPreviewedFile
} = packagingSlice.actions;

export default packagingSlice.reducer;
